import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import BASE_URL from "../../Utilities/BASE_URL";
import PageHeader from "../../Components/PageHeader/PageHeader";

const dayOptions = [
  { id: "all", label: "All" },
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
];

const priorityOptions = [
  { id: "all", label: "All" },
  { id: "important", label: "Important" },
  { id: "medium", label: "Medium" },
  { id: "normal", label: "Normal" },
];

const formatPriority = (p) => {
  switch ((p || "normal").toLowerCase()) {
    case "important":
      return "IMPORTANT";
    case "medium":
      return "MEDIUM";
    default:
      return "NORMAL";
  }
};

const getPriorityColor = (p) => {
  switch ((p || "normal").toLowerCase()) {
    case "important":
      return "#EF4444";
    case "medium":
      return "#F59E0B";
    default:
      return "#3B82F6";
  }
};

const Announcements = () => {
  const { isLoggedIn, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedDay, setSelectedDay] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");

  useEffect(() => {
    // Only redirect if not logged in AND we've finished loading auth state
    if (!authLoading && !isLoggedIn) {
      navigate("/login");
      return;
    }

    const fetchAnnouncements = async () => {
      if (!token || authLoading) return;
      try {
        setLoading(true);
        setError("");
        const res = await fetch(
          `${BASE_URL}/notifications/announcements?page=1&limit=50`,
          {
            method: "GET",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            credentials: "include",
          }
        );
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.message || "Failed to load announcements");
        const list = Array.isArray(data?.data?.announcements)
          ? data.data.announcements
          : [];
        setItems(list);
      } catch (e) {
        setError(e.message || "Failed to load announcements");
      } finally {
        setLoading(false);
      }
    };
    if (isLoggedIn) fetchAnnouncements();
  }, [isLoggedIn, token, navigate, authLoading]);

  const filteredAnnouncements = useMemo(() => {
    if (!items.length) return [];
    return items.filter((item) => {
      const createdDate = new Date(item.createdAt || item.sentAt || Date.now());
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      oneWeekAgo.setHours(0, 0, 0, 0);
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      oneMonthAgo.setHours(0, 0, 0, 0);

      if (selectedDay === "today" && createdDate < today) return false;
      if (selectedDay === "week" && createdDate < oneWeekAgo) return false;
      if (selectedDay === "month" && createdDate < oneMonthAgo) return false;

      if (
        selectedPriority !== "all" &&
        (item.priority || "normal").toLowerCase() !== selectedPriority
      )
        return false;

      return true;
    });
  }, [items, selectedDay, selectedPriority]);

  // Show loading indicator while auth state is being determined
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#003F75]"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not logged in (will redirect)
  if (!isLoggedIn) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Announcements"
        subtitle="Stay up to date with the latest updates and important notifications from Dijla"
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 mb-6">
          <div className="mb-6">
            <div className="text-sm font-medium text-gray-700 mb-3">
              Filter by time:
            </div>
            <div className="flex flex-wrap gap-2">
              {dayOptions.map((opt) => (
                <button
                  key={`day-${opt.id}`}
                  onClick={() => setSelectedDay(opt.id)}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                    selectedDay === opt.id
                      ? "bg-[#003F75] text-white border-[#003F75] shadow-sm"
                      : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-0">
            <div className="text-sm font-medium text-gray-700 mb-3">
              Filter by priority:
            </div>
            <div className="flex flex-wrap gap-2">
              {priorityOptions.map((opt) => (
                <button
                  key={`prio-${opt.id}`}
                  onClick={() => setSelectedPriority(opt.id)}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                    selectedPriority === opt.id
                      ? "bg-gray-900 text-white border-gray-900 shadow-sm"
                      : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                  }`}
                  style={{
                    borderColor:
                      opt.id !== "all" && opt.id !== "normal"
                        ? getPriorityColor(opt.id)
                        : undefined,
                    color:
                      selectedPriority !== opt.id &&
                      opt.id !== "all" &&
                      opt.id !== "normal"
                        ? getPriorityColor(opt.id)
                        : undefined,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#003F75]"></div>
            <p className="text-gray-500 mt-4">Loading announcements...</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
            {error}
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <span className="text-2xl">📢</span>
            </div>
            <p className="text-gray-500 text-lg">No announcements yet</p>
            <p className="text-gray-400 text-sm mt-2">
              Check back later for updates
            </p>
          </div>
        )}

        {!loading && items.length > 0 && filteredAnnouncements.length === 0 && (
          <div className="py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <span className="text-2xl">🔍</span>
            </div>
            <p className="text-gray-500 text-lg">No matching announcements</p>
            <p className="text-gray-400 text-sm mt-2">
              Try changing your filters
            </p>
          </div>
        )}

        <div className="space-y-4">
          {filteredAnnouncements.map((a) => (
            <div
              key={a._id || a.id}
              className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="font-semibold text-lg text-[#133B6E] line-clamp-2">
                  {a.title}
                </h3>
                <span
                  className="text-xs font-bold px-3 py-1 rounded-full text-white whitespace-nowrap"
                  style={{ backgroundColor: getPriorityColor(a.priority) }}
                >
                  {formatPriority(a.priority)}
                </span>
              </div>
              <p className="text-gray-700 line-clamp-3 mb-4">{a.body}</p>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: getPriorityColor(a.priority) }}
                />
                <time className="text-gray-400">
                  {new Date(
                    a.createdAt || a.sentAt || Date.now()
                  ).toLocaleString()}
                </time>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Announcements;
